// Frying/sauté heat-cue validator. Every step whose instruction
// uses an active high-heat verb (sear, fry, sauté, brown, deep-fry,
// stir-fry) should give the cook a way to know the pan is at the
// right temperature — either an explicit Fahrenheit target, a
// "shimmer / smoking lightly" cue, or a heat level (medium-high,
// high) that's appropriate for the verb.
//
// Without that cue, cooks default to medium and get steamed
// proteins instead of seared ones. The Maillard contract: hot pan
// + dry surface = browning. Cold pan = wet, gray failure.
//
// `brown` is special-cased. Treating it as a loose verb match
// (`\bbrown(?:ing)?\b`) caused massive collateral — brown butter,
// brown sugar, brown rice, brown lentils, "golden brown", "tiny
// brown specks" all triggered the high-heat auto-fix and silently
// appended an oil-shimmer cue to gentle / non-sear steps. We now
// only count `brown` as a sear verb when it's syntactically used
// as one: followed by a protein/aromatic object, "on/all sides",
// or "until <sear-grade adjective>".

const CORE_SEAR_VERB_RE = /\b(sear|deep[\s-]?fry|pan[\s-]?fry|stir[\s-]?fry|saut[eé])\b/i;

// `brown` AS a sear verb. Three syntactic forms count:
//   1. brown [the] <protein|aromatic>  → "brown the chicken", "brown
//      onions", "brown sausage"
//   2. brown [on|all] sides            → "brown on both sides",
//      "brown all over"
//   3. brown until <sear-grade adj>    → "brown until deeply golden",
//      "brown until well caramelized"
// "brown butter", "brown rice", "brown sugar", "brown lentils",
// "brown bread", "brown stock", "brown ale", "golden brown", "lightly
// brown", "tiny brown specks", "darker brown" all fall through.
const BROWN_SEAR_RE = new RegExp(
  // form 1: brown [the] <object>
  "\\bbrown(?:s|ed|ing)?\\s+(?:the\\s+|some\\s+)?(?:meat|beef|chicken|pork|lamb|veal|turkey|duck|sausages?|chorizo|bacon|pancetta|guanciale|protein|cutlets?|chops?|steaks?|tenderloins?|breasts?|thighs?|legs?|wings?|patty|patties|fillets?|loins?|roasts?|onions?|shallots?|leeks?|garlic|mushrooms?|mirepoix|aromatics|vegetables?)\\b" +
  // form 2: brown on/all sides
  "|\\bbrown(?:s|ed|ing)?\\s+(?:on\\s+(?:both|all|every|each)|all)\\s*sides?\\b" +
  "|\\bbrown(?:s|ed|ing)?\\s+(?:all\\s+over|evenly\\s+all\\s+over)\\b" +
  // form 3: brown until <sear-grade adj>
  "|\\bbrown(?:s|ed|ing)?\\s+(?:until|to)\\s+(?:deeply|well|nicely|evenly|golden|dark(?:ly)?|caramelized|crusty|crisp)",
  "i",
);

function isSearStep(text) {
  if (CORE_SEAR_VERB_RE.test(text)) return true;
  if (BROWN_SEAR_RE.test(text))     return true;
  return false;
}

const HEAT_CUE_RE = /\b(shimmer|smoking|smoke\b|ripple|just\s+before\s+smoking|wave[s]?\s+across)\b|\b(?:high|medium[\s-]?high|screaming)\s+heat\b|\b3[5-9][0-9]\s*°?\s*f\b|\b4[0-2][0-9]\s*°?\s*f\b/i;

function stepText(s) {
  return s?.instruction || s?.body || s?.text || "";
}

export function isFryingRecipe(recipe) {
  const steps = recipe?.steps || [];
  return steps.some((s) => isSearStep(stepText(s)));
}

export function validateFryingHeat(recipe) {
  const steps = recipe?.steps || [];
  const warnings = [];
  for (let i = 0; i < steps.length; i += 1) {
    const txt = stepText(steps[i]);
    if (!isSearStep(txt)) continue;
    // Cue can live on instruction OR heat field OR doneCue.
    const cue = `${txt} ${steps[i]?.heat || ""} ${steps[i]?.doneCue || ""}`;
    if (HEAT_CUE_RE.test(cue)) continue;
    // Also accept the step having `heat: "high"` / "medium-high"
    // already (without the regex catching it via the loose match).
    const heatField = String(steps[i]?.heat || "").toLowerCase();
    if (heatField.includes("high")) continue;
    warnings.push({
      kind: "frying_heat_cue_missing",
      severity: "warning",
      stepId: steps[i].id,
      stepIndex: i,
      message: `Step "${steps[i].title || steps[i].id}" calls for searing/frying but doesn't tell the cook the pan should be hot.`,
      suggestion: "Add a visible cue like \"until the oil shimmers and just starts to smoke\" or set heat to high / medium-high.",
    });
  }
  return { ok: true, issues: [], warnings };
}

export function autoFixFryingHeat(recipe, findings) {
  const warnings = Array.isArray(findings?.warnings) ? findings.warnings : [];
  if (warnings.length === 0) return { recipe, changes: [] };
  const steps = [...(recipe.steps || [])];
  const changes = [];
  for (const w of warnings) {
    if (w.kind !== "frying_heat_cue_missing") continue;
    if (w.stepIndex < 0 || w.stepIndex >= steps.length) continue;
    const step = { ...steps[w.stepIndex] };
    // Bump the structured heat field if it isn't already high-ish.
    const cur = String(step.heat || "").toLowerCase();
    if (!cur.includes("high")) {
      // Pick medium-high by default — universal sear/sauté target.
      // True high-heat (stir-fry / blackening / cast iron sear) the
      // recipe author can bump further by hand.
      step.heat = "medium-high";
    }
    // Append a sensory cue to the instruction so the cook has a
    // visual confirmation in addition to the heat level.
    const orig = stepText(step).trim();
    if (!HEAT_CUE_RE.test(orig)) {
      const append = " Heat the pan until the oil shimmers and a drop of water sizzles on contact.";
      if ("instruction" in step) step.instruction = orig + append;
      else if ("body" in step)   step.body        = orig + append;
      else                       step.instruction = orig + append;
    }
    steps[w.stepIndex] = step;
    changes.push({
      kind: "frying_heat_cue_missing",
      summary: `Set heat to medium-high and added an oil-shimmer cue to step "${step.title || step.id}".`,
    });
  }
  return { recipe: { ...recipe, steps }, changes };
}
